import { buildBigQueryEmitter } from '@fiverr-private/obs';
import { BUS_EVENT_NAME } from '../constants';

/**
 * The monicore event.
 * @typedef {Object} Event
 * @property {(measure|error|event)} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {number} createdAt - Time at which event start as epoch millis
 * @property {number} duration - duration of the event in millis
 * @property {string} value - The optional value of the event.
 * @property {Context} context - currency for the current request.
 */

/**
 * The monicore context.
 * @typedef {Object} Context
 * @property {string} id - The context id.
 * @property {string|number} userId - The current user id.
 * @property {string} countryCode - country code for the current request.
 * @property {string} browserName - browser name for the current request.
 * @property {string} locale - locale for the current request.
 * @property {string} currency - currency for the current request.
 * @property {(web|mobile_web)} platform - platform for the current request.
 * @property {boolean} isKnownCrawler - true if the current request is from a known crawler.
 */

/**
 * The monicore context formatted for bigQuery.
 * @typedef {Object} BigQueryEvent
 * @property {(measure|error|event)} type - The type of event.
 * @property {string} name - The name of the event.
 * @property {array} createdAt - The time when the event was created, ISO formatted.
 * @property {array} duration - The duration in milliseconds of the event.
 * @property {string} value - The optional value of the event.
 * @property {string} context_id - The context id.
 * @property {string|number} user_id - The current user id.
 * @property {string} country_code - country code for the current request.
 * @property {string} browser - browser name for the current request.
 * @property {string} locale - locale for the current request.
 * @property {string} currency - currency for the current request.
 * @property {string} platform - platform for the current request.
 */
/**
 * The monicore context formatted for bigQuery.
 * @typedef {Object} BigQueryContext
 * @property {string} context_id - The context id.
 * @property {string|number} user_id - The current user id.
 * @property {string} country_code - country code for the current request.
 * @property {string} browser - browser name for the current request.
 * @property {string} locale - locale for the current request.
 * @property {string} currency - currency for the current request.
 * @property {string} platform - platform for the current request.
 */

/**
 * Format the event context for bigQuery
 * @param {Context} context
 * @returns {BigQueryContext}
 */
const formatContext = (context) => ({
    context_id: context.id,
    user_id: context.userId.toString(),
    country_code: context.countryCode,
    browser: context.browserName,
    locale: context.locale,
    currency: context.currency,
    platform: context.platform,
});

/**
 * Format the event for bigQuery
 * @param {Event} event
 * @returns {BigQueryEvent}
 */
export const formatEvent = (event) => ({
    type: event.type,
    name: event.name,
    created_at: new Date(event.createdAt).toISOString(),
    duration: event.duration || null,
    value: event.value || null,
    ...formatContext(event.context),
});

/**
 * Format and then send the event to bigQuery
 * @param {Event} event
 */
export const send = (event) => {
    // Ignore bots events
    if (!event.context.isKnownCrawler) {
        bigQuerySend(formatEvent(event));
    }
};

/**
 * Send events via bigQuery emitter
 *
 * @example
 *
 * send({
 *   a: '1'
 * }, {
 *   b: '2'
 * });
 */
const bigQuerySend = buildBigQueryEmitter(BUS_EVENT_NAME);
